import React, { useState } from 'react';
import { RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import { clientsApi } from '../services/api';
import { UpdateResult, SyncResult } from '../types';
import './ActionBar.css';

interface ActionBarProps {
  year: number;
  month: number;
  onDataUpdated: () => void;
  onUpdateResult: (result: UpdateResult | null) => void;
}

const ActionBar: React.FC<ActionBarProps> = ({ year, month, onDataUpdated, onUpdateResult }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateStatus, setUpdateStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const handleUpdate = async () => {
    setIsUpdating(true);
    setUpdateStatus({ type: null, message: '' });

    try {
      // Paso 1: Sincronizar lista de clientes desde DigitalOcean
      setUpdateStatus({
        type: null,
        message: 'Paso 1/2: Actualizando lista de clientes desde DigitalOcean...'
      });

      const syncResponse = await clientsApi.syncFromDigitalOcean();
      
      if (!syncResponse.success) {
        throw new Error(syncResponse.error || 'Error en sincronización');
      }

      const syncResult = syncResponse.data as SyncResult;
      
      // Paso 2: Actualizar datos de todos los clientes
      setUpdateStatus({
        type: null,
        message: 'Paso 2/2: Actualizando datos de clientes...'
      });

      const updateResponse = await clientsApi.updateAllClients(year, month);
      
      if (!updateResponse.success) {
        throw new Error(updateResponse.error || 'Error en actualización de datos');
      }

      const updateResult = updateResponse.data as UpdateResult;
      
      // Mostrar resultado final
      setUpdateStatus({
        type: updateResult.errorCount > 0 ? 'error' : 'success',
        message: `Actualización completa: ${syncResult.added} nuevos clientes, ${updateResult.successCount} actualizados, ${updateResult.errorCount} errores`
      });
      
      // Pasar el resultado al componente padre
      onUpdateResult(updateResult);
      onDataUpdated();
      
    } catch (error) {
      console.error('Error in full update:', error);
      setUpdateStatus({
        type: 'error',
        message: `Error en actualización completa: ${error instanceof Error ? error.message : 'Error desconocido'}`
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="action-bar">
      <div className="action-buttons">
        <button
          onClick={handleUpdate}
          disabled={isUpdating}
          className="action-button primary"
        >
          <RefreshCw size={16} className={isUpdating ? 'spinning' : ''} />
          {isUpdating ? 'Actualizando...' : 'Actualizar'}
        </button>
      </div>

      {updateStatus.message && (
        <div className={`status-message ${updateStatus.type || 'info'}`}>
          {updateStatus.type === 'success' ? (
            <CheckCircle size={16} />
          ) : updateStatus.type === 'error' ? (
            <AlertCircle size={16} />
          ) : (
            <RefreshCw size={16} className="spinning" />
          )}
          <span>{updateStatus.message}</span>
        </div>
      )}
    </div>
  );
};

export default ActionBar;
